import React, { useState, useEffect } from "react";
import { Box, Button, IconButton, CircularProgress, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useDispatch, useSelector } from "react-redux";
import { useAddToCart, useGetProductInventoryDetails, useRemoveFromCart } from "./globalFunctions";
import getBrand from "../brands/getBrand";

const AddToCartButton = ({
  productId,
  inventoryId,
  inventoryDetailsId,
  selectedCapacity,
  totalAmount = 0,
  quantities = 0,
  mainProduct = null,
  disabled = false,
  disableIncrement = false,
  onUpdateCart,
  onStockLimit,  
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.id);
  const userCart = useSelector((state) => state.user.userCart);
  const selectedHub = useSelector((state) => state.user.selected_hub);
  const brand = getBrand();
  const addToCart = useAddToCart();
  const getProductInventoryDetails = useGetProductInventoryDetails();
  const removeFromCart = useRemoveFromCart();
  const [quant, setQuant] = useState(quantities);
  const [loading, setLoading] = useState(false);

  // Get theme colors from brand configuration
  const primaryColor = brand?.themeColor || "#1976d2";
  const disabledColor = primaryColor + "80"; // 50% opacity for disabled state

  // Update quantity from Redux cart when it changes (after cart actions)
  useEffect(() => {
    if (Array.isArray(userCart) && userCart.length > 0) {
      const cartItem = userCart.find((item) => item.product_id === productId);
      if (cartItem) {
        const matchedCapacity = cartItem.capacity?.find(
          (c) => c.item_capacity === selectedCapacity?.item_capacity
        );
        if (matchedCapacity) {
          setQuant(matchedCapacity.item_quantity || 0);
        } else {
          setQuant(0);
        }
      } else {
        setQuant(0);
      }
    } else if (Array.isArray(userCart) && userCart.length === 0) {
      setQuant(0);
    }
  }, [userCart, productId, selectedCapacity?.item_capacity]);

  const resolveInventoryDetailsId = async () => {
    if (inventoryDetailsId) return inventoryDetailsId;

    const inventoryResponse = await getProductInventoryDetails(inventoryId, productId);

    if (inventoryResponse?.success) {
      const inventoryDetails = inventoryResponse.data;
      const matchedDetail = inventoryDetails.find((detail) => detail?.product === productId);
      if (matchedDetail) {
        return matchedDetail.id;
      }
    }
    return null;
  };

  const applyQuantityChange = async ({ delta, type }) => {
    if (disabled || loading) return;

    const currentQuantity = parseInt(quant) || 0;
    const newQuantity = currentQuantity + delta;

    try {
      setLoading(true);
      setQuant(newQuantity); // Optimistic update

      const finalInventoryDetailsId = await resolveInventoryDetailsId();

      if (!finalInventoryDetailsId) {
        setQuant(currentQuantity);
        alert("Product inventory details not found");
        setLoading(false);
        return;
      }

      const result = await addToCart({
        userId,
        inventoryId,
        productId,
        quantity: newQuantity,
        inventoryDetailsId: finalInventoryDetailsId,
        capacity: selectedCapacity,
        type,
        mainProduct: mainProduct,
        totalAmount: totalAmount,
      });

      if (result?.limited) {
        setQuant(currentQuantity);
        if (onStockLimit) {
          onStockLimit();
        }
        setLoading(false);
        return;
      }

      if (result?.success) {
        if (onUpdateCart) {
          onUpdateCart();
        }
      } else {
        setQuant(currentQuantity);
        alert(result.message || "Failed to update cart");
      }
    } catch (error) {
      console.error("Error updating cart:", error);
      setQuant(currentQuantity);
      alert("Failed to update cart");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (disableIncrement) {
        if (onStockLimit) {
          onStockLimit();
        }
        return;
      }
    applyQuantityChange({ delta: 1, type: "add" });
  };


  const handleIncrement = () => {
    if (disableIncrement) {
      if (onStockLimit) {
        onStockLimit();
      }
      return;
    }

    applyQuantityChange({ delta: 1, type: "add" });
  };

  const handleDecrement = async () => {
    if (disabled || loading) return;

    const currentQuantity = parseInt(quant) || 0;
    if (currentQuantity <= 1) {
      // Remove from cart
      handleRemove();
      return;
    }

    try {
      setLoading(true);
      const newQuantity = currentQuantity - 1;

      // Optimistic update
      setQuant(newQuantity);

      const result = await addToCart({
        userId,
        inventoryId,
        productId,
        quantity: newQuantity,
        inventoryDetailsId,
        capacity: selectedCapacity,
        type: "sub",
        mainProduct: mainProduct,
        totalAmount: totalAmount,
      });
      // console.log("AddToCart result:", result);

        if (result?.success) {
          // Call callback to fetch updated cart from API (prevents double API call)
          if (onUpdateCart) {
            onUpdateCart();
          }
        } else if (!result?.success) {
          // Revert on error
          setQuant(currentQuantity);
          alert(result.message || "Failed to update cart");
        }
    } catch (error) {
      console.error("Error decrementing cart:", error);
      // Revert optimistic update
      setQuant(parseInt(quant) + 1);
      alert("Failed to update cart");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (disabled || loading) return;

    try {
      setLoading(true);
      const newQuantity = 0;

      // Optimistic update
      setQuant(newQuantity);

      const result = await removeFromCart({
        userId,
        inventoryId,
        productId,
        quantity: newQuantity,
        capacity: selectedCapacity,
        mainProduct: mainProduct,
      });

      if (result?.success) {
        // Call callback to fetch updated cart from API (prevents double API call)
        if (onUpdateCart) {
          onUpdateCart();
        }
      } else {
        // Revert on error
        setQuant(parseInt(quant) + 1);
        alert(result.message || "Failed to remove from cart");
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
      // Revert optimistic update
      setQuant(parseInt(quant) + 1);
      alert("Failed to remove from cart");
    } finally {
      setLoading(false);
    }
  };

  if (quant > 0) {
    return (
      <>

        {quant > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              minWidth: "82px",
              height: "29px",
              borderRadius: "20px",
              backgroundColor: loading || disabled ? disabledColor : primaryColor,
            }}
          >
            {/* Minus */}
            <IconButton
              size="small"
              onClick={handleDecrement}
              disabled={loading}
              sx={{ color: "white" }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            {loading ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              <Typography sx={{ color: "white", fontWeight: 600 }}>
                {quant}
              </Typography>
            )}

            {/* Plus */}
            <IconButton
              size="small"
              onClick={handleIncrement}
              disabled={loading || disabled}
              sx={{ color: "white" }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={loading || disabled}
            sx={{
              minWidth: "82px",
              height: "29px",
              borderRadius: "20px",
              backgroundColor: loading || disabled ? disabledColor : primaryColor,
              color: "white",
              fontSize: "14px",
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              "Add"
            )}
          </Button>
        )}
      </>
    );

  }

  return (
    <Button
      variant="contained"
      onClick={handleAdd}
      disabled={loading || disabled || disableIncrement}
      sx={{
        minWidth: "82px",
        height: "29px",
        borderRadius: "20px",
        backgroundColor: loading || disabled ? disabledColor : primaryColor,
        color: "white",
        fontSize: "14px",
        textTransform: "none",
        padding: "0",
      }}
    >
      {loading ? (
        <CircularProgress size={16} sx={{ color: "white" }} />
      ) : (
        "Add"
      )}
    </Button>
  );
};

export default AddToCartButton;

